import Container from "../../../components/element/container";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/element/icon";
import SubmittedMedia from "../../../components/common/submitted.media";
import useMediaUrl from "../../../components/hook/use.media.url";

export default function SignatureUpload({
  value,
  onChange,
  disabled,
  onLoadingChange,
  readOnly = false,
  document: submittedDocument,
}) {
  const { t } = useTranslation();
  const submittedUrl = useMediaUrl(submittedDocument);
  const input = useRef(null);
  const request = useRef(0);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  useEffect(
    () => () => {
      request.current += 1;
    },
    [],
  );
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const selectFile = (file) => {
    if (!file || disabled) return;
    const id = ++request.current;
    setError("");
    setLoading(false);
    if (
      !["image/png", "image/jpeg", "image/gif", "image/webp"].includes(
        file.type,
      )
    ) {
      setError("Choose a PNG, JPG, GIF or WebP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Choose an image smaller than 5 MB.");
      return;
    }
    setLoading(true);
    const failed = () => {
      if (request.current !== id) return;
      setLoading(false);
      setError("Unable to read this image. Please choose another file.");
    };
    const reader = new FileReader();
    reader.onerror = failed;
    reader.onload = () => {
      const image = new Image();
      image.onerror = failed;
      image.onload = () => {
        if (request.current !== id) return;
        // Normalize WebP and large images to a PNG supported by the PDF renderer.
        try {
          const ratio = Math.min(
            1,
            1600 / Math.max(image.naturalWidth, image.naturalHeight),
          );
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
          canvas
            .getContext("2d")
            .drawImage(image, 0, 0, canvas.width, canvas.height);
          onChange(canvas.toDataURL("image/png"), file);
          setFileName(file.name);
          setLoading(false);
        } catch {
          failed();
        }
      };
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  if (readOnly)
    return (
      <Container tag="div" className="vso-upload-signature">
        <Container tag="div" className="vso-upload-zone">
          {submittedUrl ? (
            <>
              <img
                className="vso-upload-preview"
                src={submittedUrl}
                alt="Uploaded signature"
              />
              <SubmittedMedia
                document={submittedDocument}
                icon="solar:gallery-linear"
              />
            </>
          ) : (
            <Container tag="div" className="vso-upload-copy">
              <strong>{t("Signature image")}</strong>
              <small>
                {t(
                  "No separate signature image available. Refer to the signed VSO.",
                )}
              </small>
            </Container>
          )}
        </Container>
      </Container>
    );

  return (
    <Container tag="div" className="vso-upload-signature">
      <Container tag="div"
        className={`vso-upload-zone ${dragging ? "is-dragging" : ""} ${value ? "has-image" : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          selectFile(event.dataTransfer.files?.[0]);
        }}
      >
        <span className="vso-upload-icon" aria-hidden="true">
          <Icon
            icon={
              value ? "solar:gallery-check-linear" : "solar:gallery-add-linear"
            }
          />
        </span>
        <Container tag="div" className="vso-upload-copy">
          <strong>
            {loading
              ? t("Preparing image…")
              : value
                ? fileName
                : t("Upload signature image (optional)")}
          </strong>
          {/* The file name says enough once an image is attached. */}
          {!value && <small>{t("PNG or JPG up to 5 MB")}</small>}
        </Container>
        <Container tag="div" className="vso-upload-actions">
          {value ? (
            <button
              type="button"
              className="vso-upload-remove"
              disabled={disabled}
              onClick={() => {
                request.current += 1;
                setLoading(false);
                setFileName("");
                setError("");
                onChange("");
              }}
            >
              {t("Remove")}
            </button>
          ) : (
            <button
              type="button"
              disabled={disabled || loading}
              onClick={() => input.current?.click()}
            >
              {t("Upload")}
            </button>
          )}
        </Container>
        <input
          ref={input}
          type="file"
          hidden
          aria-label="Upload signature image"
          accept="image/png,image/jpeg,image/gif,image/webp"
          disabled={disabled || loading}
          onChange={(event) => {
            selectFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </Container>
      {/* {value && <img className="vso-upload-preview" src={value} alt="Uploaded signature" />} */}
      {error && (
        <p className="vso-upload-error" role="alert">
          {error}
        </p>
      )}
    </Container>
  );
}
